import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileText, Trash2, Download, Clock, User, File } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ProjectFilesViewer = ({ project, onUpdate, shareCode }) => {
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    category: 'architectural',
    description: '',
    uploaded_by: ''
  });
  
  const categories = [
    { id: 'architectural', label: 'Architectural' },
    { id: 'structural', label: 'Structural' },
    { id: 'mechanical', label: 'Mechanical' },
    { id: 'electrical', label: 'Electrical' },
    { id: 'plumbing', label: 'Plumbing' },
    { id: 'civil', label: 'Civil' },
    { id: 'landscape', label: 'Landscape' },
    { id: 'other', label: 'Other' }
  ];
  
  const files = project.files || [];
  
  const getFilesByCategory = (category) => {
    return files.filter(f => f.category === category);
  };
  
  const handleFileUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    
    if (!uploadData.uploaded_by || uploadData.uploaded_by.trim() === '') {
      toast.error('Please enter your name');
      return;
    }
    
    setUploading(true);
    
    try {
      for (const file of Array.from(selectedFiles)) {
        // Read file as base64
        const reader = new FileReader();
        const fileData = await new Promise((resolve, reject) => {
          reader.onload = (event) => {
            resolve({
              filename: file.name,
              original_filename: file.name,
              file_type: file.name.split('.').pop().toLowerCase(),
              file_size: file.size,
              category: uploadData.category,
              description: uploadData.description,
              uploaded_by: uploadData.uploaded_by,
              file_url: event.target?.result
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        await api.uploadFile(project.id, fileData);
      }
      
      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setShowUploadModal(false);
      setUploadData({ category: 'architectural', description: '', uploaded_by: uploadData.uploaded_by });
      onUpdate();
    } catch (error) {
      toast.error('Error uploading files');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };
  
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await api.deleteFile(project.id, fileId);
      toast.success('File deleted');
      onUpdate();
    } catch (error) {
      toast.error('Error deleting file');
    }
  };
  
  const handleDownloadFile = (file) => {
    // Create download link
    const link = document.createElement('a');
    link.href = file.file_url;
    link.download = file.original_filename;
    link.click();
  };
  
  return (
    <div className="space-y-6">
      {/* Upload Button */}
      <Card className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
              Upload Project Files
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Share drawings, specifications, and documents with your team
            </p>
          </div>
          
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Files
          </Button>
        </div>
      </Card>
      
      {/* Upload Modal */}
      {showUploadModal && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Upload New Files
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowUploadModal(false)}
            >
              Cancel
            </Button>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="uploader-name">Your Name *</Label>
              <Input
                id="uploader-name"
                value={uploadData.uploaded_by}
                onChange={(e) => setUploadData({ ...uploadData, uploaded_by: e.target.value })}
                placeholder="e.g., John Smith"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category *</Label>
              <select
                id="category"
                value={uploadData.category}
                onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="Brief description of the files..."
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="file-input">Select Files</Label>
              <Input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                className="cursor-pointer"
              />
              <p className="text-xs text-slate-500 mt-1">
                Supported: DWG, PDF, JPG, PNG, and more
              </p>
            </div>
          </div>
        </Card>
      )}
      
      {/* Files by Category */}
      <Tabs defaultValue="architectural" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8">
          {categories.map(cat => (
            <TabsTrigger key={cat.id} value={cat.id} className="text-xs">
              <span className="hidden md:inline">{cat.label}</span>
              <span className="md:hidden">{cat.label.substring(0, 3)}</span>
              {getFilesByCategory(cat.id).length > 0 && (
                <span className="ml-1 text-slate-600 font-medium">
                  ({getFilesByCategory(cat.id).length})
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {categories.map(category => (
          <TabsContent key={category.id} value={category.id} className="space-y-4 mt-6">
            {getFilesByCategory(category.id).length === 0 ? (
              <Card className="p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
                <File className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  No {category.label.toLowerCase()} files uploaded yet
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setUploadData({ ...uploadData, category: category.id });
                    setShowUploadModal(true);
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload {category.label} Files
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getFilesByCategory(category.id).map((file) => (
                  <Card key={file.id} className="p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                            {file.original_filename}
                          </h4>
                          <p className="text-xs text-slate-500 uppercase">
                            {file.file_type} • {(file.file_size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {file.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                        {file.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                      <div className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        {file.uploaded_by}
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {format(new Date(file.uploaded_at), 'MMM dd, yyyy')}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadFile(file)}
                        className="flex-1"
                      >
                        <Download className="mr-2 h-3 w-3" />
                        Download
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteFile(file.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ProjectFilesViewer;